import { Email } from '../types/email';
import { sampleEmails } from '../data/sampleEmails';

export class EmailService {
  private static instance: EmailService;
  private emails: Email[] = [...sampleEmails];

  private constructor() {
    // Load emails from localStorage if available
    const storedEmails = localStorage.getItem('emails');
    if (storedEmails) {
      this.emails = JSON.parse(storedEmails);
    }
  }

  private saveEmails() {
    localStorage.setItem('emails', JSON.stringify(this.emails));
  }

  public static getInstance(): EmailService {
    if (!EmailService.instance) {
      EmailService.instance = new EmailService();
    }
    return EmailService.instance;
  }

  public async getEmails(folder: string): Promise<Email[]> {
    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 500));
    
    return this.emails.filter(email => {
      if (folder === 'starred') return email.isStarred;
      return email.folder === folder;
    }).sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  }

  public async sendEmail(email: Omit<Email, 'id' | 'timestamp' | 'folder'>): Promise<void> {
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    const newEmail: Email = {
      ...email,
      id: crypto.randomUUID(),
      timestamp: new Date().toISOString(),
      folder: 'sent',
      isRead: true,
      isStarred: false
    };

    this.emails.unshift(newEmail);
    this.saveEmails();

    // Create received copy for demo
    const receivedEmail: Email = {
      ...newEmail,
      id: crypto.randomUUID(),
      folder: 'inbox',
      isRead: false,
      from: email.to[0],
      to: [email.from]
    };
    
    setTimeout(() => {
      this.emails.unshift(receivedEmail);
      this.saveEmails();
    }, 2000);
  }

  public async moveToFolder(emailId: string, folder: Email['folder']): Promise<void> {
    await new Promise(resolve => setTimeout(resolve, 500));
    
    const emailIndex = this.emails.findIndex(e => e.id === emailId);
    if (emailIndex !== -1) {
      this.emails[emailIndex] = { ...this.emails[emailIndex], folder };
      this.saveEmails();
    }
  }

  public async toggleStar(emailId: string): Promise<void> {
    const emailIndex = this.emails.findIndex(e => e.id === emailId);
    if (emailIndex !== -1) {
      this.emails[emailIndex] = {
        ...this.emails[emailIndex],
        isStarred: !this.emails[emailIndex].isStarred
      };
      this.saveEmails();
    }
  }

  public async markAsRead(emailId: string): Promise<void> {
    const emailIndex = this.emails.findIndex(e => e.id === emailId);
    if (emailIndex !== -1) {
      this.emails[emailIndex] = { ...this.emails[emailIndex], isRead: true };
      this.saveEmails();
    }
  }

  public async deleteEmail(emailId: string): Promise<void> {
    await new Promise(resolve => setTimeout(resolve, 500));
    
    const emailIndex = this.emails.findIndex(e => e.id === emailId);
    if (emailIndex !== -1) {
      this.emails[emailIndex] = { ...this.emails[emailIndex], folder: 'trash' };
      this.saveEmails();
    }
  }

  public searchEmails(term: string, folder: string): Email[] {
    const folderEmails = this.emails.filter(email => {
      if (folder === 'starred') return email.isStarred;
      return email.folder === folder;
    });

    if (!term) return folderEmails;

    const searchTerm = term.toLowerCase();
    return folderEmails.filter(email =>
      email.subject.toLowerCase().includes(searchTerm) ||
      email.from.toLowerCase().includes(searchTerm) ||
      email.content.toLowerCase().includes(searchTerm) ||
      email.to.some(recipient => recipient.toLowerCase().includes(searchTerm))
    );
  }
}